import React from 'react';
import { StepProps } from '../MultiStepModal';
import { 
  UserIcon, 
  CalendarDaysIcon, 
  ClockIcon,
  BuildingOfficeIcon,
  ExclamationTriangleIcon
} from '@heroicons/react/24/outline';

export default function ReviewStep({ data, onComplete }: StepProps) {
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onComplete();
  };

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'low': return '#6B7280';
      case 'standard': return '#3B82F6';
      case 'high': return '#F59E0B';
      case 'urgent': return '#EF4444';
      default: return '#6B7280';
    }
  };

  const getEngineerName = (engineerId: string) => {
    const engineers = {
      '1': 'Mike Johnson',
      '2': 'David Brown',
      '3': 'James Wilson',
      '4': 'Tom Anderson',
      '5': 'Chris Martin'
    };
    return engineers[engineerId as keyof typeof engineers] || 'Unknown Engineer';
  };

  return (
    <div className="step-form">
      <div className="step-header">
        <h3 className="step-form-title">Review & Confirm</h3>
        <p className="step-form-subtitle">Please review the job details before creating</p>
      </div>

      <form onSubmit={handleSubmit} className="step-form-content">
        <div className="review-section">
          <h4 className="review-section-title">Job Details</h4>
          <div className="review-grid">
            <div className="review-item">
              <span className="review-label">Title:</span>
              <span className="review-value">{data.title}</span>
            </div>
            <div className="review-item">
              <span className="review-label">Type:</span>
              <span className="review-value">{data.type}</span>
            </div>
            <div className="review-item">
              <span className="review-label">Priority:</span>
              <span 
                className="review-value priority-badge"
                style={{ color: getPriorityColor(data.priority) }}
              >
                {data.priority?.charAt(0).toUpperCase() + data.priority?.slice(1)}
              </span>
            </div>
            {data.duration && (
              <div className="review-item">
                <span className="review-label">Duration:</span>
                <span className="review-value">{data.duration} hours</span>
              </div>
            )}
          </div>
          {data.description && (
            <div className="review-item full-width">
              <span className="review-label">Description:</span>
              <p className="review-description">{data.description}</p>
            </div>
          )}
        </div>

        <div className="review-section">
          <h4 className="review-section-title">
            <UserIcon className="w-4 h-4 inline mr-2" />
            Assignment
          </h4>
          <div className="review-grid">
            <div className="review-item">
              <span className="review-label">Engineer:</span>
              <span className="review-value">{getEngineerName(data.engineerId)}</span>
            </div>
            <div className="review-item">
              <span className="review-label">
                <CalendarDaysIcon className="w-4 h-4 inline mr-1" />
                Date:
              </span>
              <span className="review-value">{data.scheduledDate}</span>
            </div>
            <div className="review-item">
              <span className="review-label">
                <ClockIcon className="w-4 h-4 inline mr-1" />
                Time:
              </span>
              <span className="review-value">{data.scheduledTime}</span>
            </div>
          </div>
          {data.instructions && (
            <div className="review-item full-width">
              <span className="review-label">Instructions:</span>
              <p className="review-description">{data.instructions}</p>
            </div>
          )}
        </div>

        <div className="review-section">
          <h4 className="review-section-title">
            <BuildingOfficeIcon className="w-4 h-4 inline mr-2" />
            Customer Information
          </h4>
          <div className="review-grid">
            <div className="review-item">
              <span className="review-label">Name:</span>
              <span className="review-value">{data.customerName}</span>
            </div>
            {data.customerCompany && (
              <div className="review-item">
                <span className="review-label">Company:</span>
                <span className="review-value">{data.customerCompany}</span>
              </div>
            )}
            {data.customerPhone && (
              <div className="review-item">
                <span className="review-label">Phone:</span>
                <span className="review-value">{data.customerPhone}</span>
              </div>
            )}
            {data.customerEmail && (
              <div className="review-item">
                <span className="review-label">Email:</span>
                <span className="review-value">{data.customerEmail}</span>
              </div>
            )}
          </div>
          <div className="review-item full-width">
            <span className="review-label">Address:</span>
            <p className="review-description">{data.customerAddress}</p>
          </div>
        </div>

        <div className="review-notice">
          <ExclamationTriangleIcon className="w-5 h-5 text-orange-500" />
          <div>
            <p className="notice-title">Ready to create job?</p>
            <p className="notice-text">
              Once created, the engineer will be notified and the job will appear in the schedule.
            </p>
          </div>
        </div>
      </form>
    </div>
  );
}
